import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { HttpService } from '@app/services/http.service';
import { MatDialog } from '@angular/material/dialog';
import { ChartConflictDialogComponent, ChartConflictData, ChartConflictResult } from '@app/shared/components/patient-chart/chart-conflict-dialog.component';
import { DomSanitizer } from '@angular/platform-browser';
import { firstValueFrom, Subject } from 'rxjs';
import { AuthService } from '@app/services/auth.service';
import { timeout } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ChartUploadService {

	static readonly failDelay: number = 15000;
	static readonly localStorageName: string = "charts:pending-upload";
	static readonly defaultUploadTimeoutMs: number = 30000;

	currentlyUploading: boolean = false;
	private readonly chartUploadedSubject = new Subject<number>();
	public readonly chartUploaded$ = this.chartUploadedSubject.asObservable();
	private pendingTimeout: any = null;

	constructor(
		private httpService: HttpService,
		private dialog: MatDialog,
		private domSanitizer: DomSanitizer,
		private authService: AuthService,
	) {
		if (!window.isSecureContext) {
			// Crypto not available in a non-secure context, so don't try to load anything
			return;
		}

		this.authService.isAuthenticated$.subscribe(isAuthenticated => {
			if (isAuthenticated) {
				this.nextPending(0);
			} else if (this.pendingTimeout !== null) {
				clearTimeout(this.pendingTimeout);
				this.pendingTimeout = null;
			}
		});
	}

	public async uploadChart(patientId: number, image) {
		try {
			const timestamp = new Date();
			if (window.isSecureContext) {
				const toUpload = await this.readLocalStorage();
				if (toUpload.some(x => x.patientId == patientId)) {
					return Promise.reject("existing");
				}

				await this.modifyLocalStorage(toUpload => {
					toUpload.push({ patientId: patientId, image: image, timestamp  });
				});
			}

			return this.startChartUpload(patientId, image);
		} catch(e) {
			return Promise.reject(e);
		}
	}

	private nextPending(delay: number) {
		if (!this.authService.userIsAuthenticated() || !window.isSecureContext) {
			return;
		}

		if (this.pendingTimeout !== null) {
			clearTimeout(this.pendingTimeout);
		}

		this.pendingTimeout = setTimeout(async () => {
			this.pendingTimeout = null;

			try {
				let toUpload = await this.readLocalStorage();
				if (toUpload[0] != undefined) {
					await this.startChartUpload(toUpload[0].patientId, toUpload[0].image, new Date(toUpload[0].timestamp))
				}
			} catch (e) {
				if (e === "malformed") {
					await this.modifyLocalStorage(toUpload => {
						toUpload.shift();
					});
					this.nextPending(0);
				}
			}
		}, delay);
	}

	/*
	private async startChartUpload(patientId: number, image, forceTime: Date = undefined) {
		if (!this.authService.userIsAuthenticated()) {
			throw new Error("not-authenticated");
		} else if (!navigator.onLine || this.currentlyUploading) {
			this.nextPending(ChartUploadService.failDelay);
			throw new Error();
		} else {
			const urlChartAdd = this.httpService.createUrl([
				'api',
				'patients',
				patientId,
				'chart'
			],
				{},
				{ host: environment.host_api_default }
			);

			let formData: FormData = new FormData();
			try {
				formData.append('chart', image.blob);
			} catch {
				throw new Error("malformed");
			}

			const headers = { };
			if (forceTime !== undefined) {
				headers['If-Unmodified-Since'] = forceTime.toUTCString();
			}

			const success = async () => {
				this.currentlyUploading = false;
				if (window.isSecureContext) {
					await this.modifyLocalStorage(toUpload => {
						return toUpload.filter(x => x.patientId != patientId);
					});
				}

				this.chartUploadedSubject.next(patientId);
				this.nextPending(0);
			};

			this.currentlyUploading = true;
			let update = this.httpService.post(urlChartAdd, formData, { headers, responseType: 'blob' }).pipe(
				timeout(this.getUploadTimeoutMs())
			);
			update.subscribe(success, async error => {
				if (error.status == 409) {
					// Conflict. We need to know what to do before retrying
					const timestamp = new Date(Date.parse(error.headers.get('last-modified')));
					const remoteChart = this.domSanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(error.error));
					if (await this.handleConflict(patientId, image, remoteChart, timestamp) === 'keep-local') {
						this.currentlyUploading = false;
						await this.modifyLocalStorage(toUpload => {
							return toUpload.filter(x => x.patientId != patientId); // Need to remove conflicting one so we can stuff a new timestamp in
						});
						await this.startChartUpload(patientId, image, new Date());
					} else {
						await success();
					}
				} else if (error.name === 'TimeoutError') {
					this.currentlyUploading = false;
					this.nextPending(ChartUploadService.failDelay);
					throw new Error("timeout");
				} else {
					this.currentlyUploading = false;
					this.nextPending(ChartUploadService.failDelay);
					throw new Error();
				}
			});
		}
	}
	*/

	private async startChartUpload(patientId: number, image, forceTime: Date = undefined) {
		if (!this.authService.userIsAuthenticated()) {
			throw "not-authenticated";
		} else if (!navigator.onLine || this.currentlyUploading) {
			this.nextPending(ChartUploadService.failDelay);
			throw "pending";
		}

		const urlChartAdd = this.httpService.createUrl([
			'api',
			'patients',
			patientId,
			'chart'
		],
			{},
			{ host: environment.host_api_default }
		);

		let formData: FormData = new FormData();
		try {
			formData.append('chart', image.blob);
		} catch {
			throw "malformed";
		}

		const headers = { };
		if (forceTime !== undefined) {
			headers['If-Unmodified-Since'] = forceTime.toUTCString();
		}

		const success = async () => {
			this.currentlyUploading = false;
			if (window.isSecureContext) {
				await this.modifyLocalStorage(toUpload => {
					return toUpload.filter(x => x.patientId != patientId);
				});
			}

			this.chartUploadedSubject.next(patientId);
			this.nextPending(0);
		};

		this.currentlyUploading = true;

		try {
			await firstValueFrom(this.httpService.post(urlChartAdd, formData, { headers, responseType: 'blob' }).pipe(
				timeout(this.getUploadTimeoutMs())
			));
			await success();
		} catch (error) {
			if (error?.status == 409) {
				// Conflict. We need to know what to do before retrying.
				const lastModified = error.headers?.get?.('last-modified');
				const timestamp = lastModified ? new Date(Date.parse(lastModified)) : new Date();
				const remoteChartUrl = window.URL.createObjectURL(error.error);
				const remoteChart = this.domSanitizer.bypassSecurityTrustResourceUrl(remoteChartUrl);
				const conflictResult = await this.handleConflict(patientId, image, remoteChart, timestamp);
				window.URL.revokeObjectURL(remoteChartUrl);

				if (conflictResult === 'keep-local') {
					this.currentlyUploading = false;
					await this.startChartUpload(patientId, image);
				} else {
					await success();
				}
			} else if (error?.name === 'TimeoutError') {
				this.currentlyUploading = false;
				this.nextPending(ChartUploadService.failDelay);
				throw "timeout";
			} else {
				this.currentlyUploading = false;
				this.nextPending(ChartUploadService.failDelay);
				throw "pending";
			}
		}
	}

	private handleConflict(patientId: number, localImage, remoteImage, remoteTimestamp: Date): Promise<ChartConflictResult> {
		return new Promise<ChartConflictResult>(resolve => {
			const data: ChartConflictData = {
				patientId: patientId,
				localImage: localImage.image,
				remoteImage: remoteImage,
				remoteLastModified: remoteTimestamp,
			};

			const dialogRef = this.dialog.open(ChartConflictDialogComponent, {
				width: '700px',
				disableClose: true,
				data: data
			});

			dialogRef.afterClosed().subscribe((result: ChartConflictResult) => {
				resolve(result);
			});
		});
	}

	private getUploadTimeoutMs(): number {
		return environment.chart_upload_timeout_ms || ChartUploadService.defaultUploadTimeoutMs;
	}

	private async readLocalStorage() {
		const encryptionKey = this.authService.getUserEncryptionKey();
		const user = this.authService.getCurrentUser();
		if (!encryptionKey || !user) {
			throw new Error("not-authenticated");
		}

		let toUpload = undefined;
		try {
			const encrypted = JSON.parse(localStorage.getItem(`${ChartUploadService.localStorageName}:${user.user_id}`));
			const data = await crypto.subtle.decrypt(
				{ name: "AES-GCM", iv: Uint8Array.from(atob(encrypted.iv), x => x.charCodeAt(0)) },
				encryptionKey,
				Uint8Array.from(atob(encrypted.data), x => x.charCodeAt(0))
			);
			toUpload = JSON.parse(Array.from(new Uint8Array(data), x => String.fromCharCode(x)).join(""));
		} catch { }
		if (!toUpload) {
			toUpload = [];
		}

		for (let chart of toUpload) {
			// The blob doesn't survive JSON.stringify, so we need to regenerate it
			const byteString = atob( chart.image.image.split(',')[1]);
			const imageBytes = Uint8Array.from(byteString, x => x.charCodeAt(0));
			chart.image.blob = new Blob([imageBytes], {'type': chart.image.mime});
		}

		return toUpload;
	}

	private async modifyLocalStorage(func) {
		const encryptionKey = this.authService.getUserEncryptionKey();
		const user = this.authService.getCurrentUser();
		if (!encryptionKey || !user) {
			throw new Error("not-authenticated");
		}

		let toUpload = await this.readLocalStorage();
		toUpload = func(toUpload) ?? toUpload;

		const iv = new Uint8Array(12);
		crypto.getRandomValues(iv);
		const encrypted = await crypto.subtle.encrypt(
			{ name: "AES-GCM", iv },
			encryptionKey,
			Uint8Array.from(JSON.stringify(toUpload), x => x.charCodeAt(0))
		);

		localStorage.setItem(`${ChartUploadService.localStorageName}:${user.user_id}`, JSON.stringify({
			iv: btoa(Array.from(new Uint8Array(iv), x => String.fromCharCode(x)).join("")),
			data: btoa(Array.from(new Uint8Array(encrypted), x => String.fromCharCode(x)).join(""))
		}));
	}
}
